import z from "zod";

export type MandateFields<T> = {
  [K in keyof T]?: true | MandateFields<T[K]>;
} & {
  oneOf?: string[][];
};

export function createSchemaWithMandates<T extends z.ZodType>(
  baseSchema: T,
  mandates?: MandateFields<z.infer<T>>
) {
  if (!mandates) return baseSchema;

  return baseSchema.superRefine((data, ctx) => {
    enforceMandates(data, mandates, ctx, []);
  });
}

export function enforceMandates<T>(
  data: T,
  mandates: MandateFields<T>,
  ctx: z.RefinementCtx,
  path: (string | number)[]
) {
  // per-field mandates
  for (const key in mandates) {
    if (key === "oneOf") continue; // skip special key

    const mandate = mandates[key as keyof T];
    const value = (data as any)?.[key];

    if (mandate === true) {
      if (
        value === undefined ||
        value === null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${key} is required`,
          path: [...path, key],
        });
      }
    } else if (typeof mandate === "object") {
      enforceMandates(value, mandate as any, ctx, [...path, key]);
    }
  }

  // oneOf groups at this level
  mandates.oneOf?.forEach((group) => {
    const satisfied = group.some((field) => {
      const val = (data as any)?.[field];
      return val !== undefined && val !== null && val !== "";
    });

    if (!satisfied) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `At least one of (${group.join(", ")}) is required`,
        path: [...path, group[0]], // error path relative to this object
      });
    }
  });
}
